import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { EGDSLink } from '../../link/link/index.esm.js';
import { EGDSLinkSizes } from '../../link/types/index.esm.js';
import { EGDSIcon } from '../../icons/icon/index.esm.js';
import { EGDSExpandoTriggerStates } from '../types/index.esm.js';
import styles from '../../styles/uitk-egds-expando.css';
import { classNames } from '../../utils/classnames/index.esm.js';
import { analyticsTracker } from '../../utils/analytics-tracker/index.esm.js';
const getClassNameList = classNames.bind(styles);
/**
 * This component contains a details disclosure which when clicked will expand and show a
 * hidden description. Expansion is determined by the boolean value of {@code isVisible}.
 *
 * @param className the provided classes to be used in both the title and the description
 *
 * In the case that the user disabled JavaScript, the details disclosure default behaviour
 * will be active. This is akin to the JavaScript enabled behaviour.
 */
const getTestId = (testId, isOpen) => isOpen ? `${testId}-expanded` : `${testId}-collapsed`;
const EGDSExpando = ({
  analytics,
  alignment,
  expandoDescription,
  expandoTitle,
  className,
  children,
  isVisible,
  isDisabled,
  isIndented = true,
  onToggle,
  triggerPosition,
  type,
  size = EGDSLinkSizes.get('small'),
  collapsedAccessibilityText,
  expandedAccessibilityText,
  ...attrs
}) => {
  const localAnalyticsId = isVisible ? EGDSExpandoTriggerStates.get('collapse') : EGDSExpandoTriggerStates.get('expand');
  const onToggleHandler = event => {
    if (event?.currentTarget?.open !== isVisible) {
      onToggle?.();
      const expandoAnalytics = analytics && {
        localAnalyticsId,
        ...analytics
      };
      expandoAnalytics && analyticsTracker(expandoAnalytics);
    }
  };
  const expandoTriggerIcon = jsx(EGDSIcon, {
    className: getClassNameList(`uitk-expando-trigger-icon${isVisible ? ' uitk-expando-trigger-icon-active' : ''}`),
    "data-testid": isVisible ? 'uitk-expando-button-icon-expanded' : undefined,
    name: "expand_more"
  });
  const a11yLabel = isVisible ? expandedAccessibilityText : collapsedAccessibilityText;
  const title = jsxs(Fragment, {
    children: [triggerPosition === 'leading' && expandoTriggerIcon, jsx("span", {
      className: getClassNameList('uitk-expando-title'),
      "aria-label": a11yLabel || undefined,
      children: expandoTitle
    }), triggerPosition === 'trailing' && expandoTriggerIcon]
  });
  return jsxs("details", {
    open: isDisabled ? !isDisabled : isVisible,
    onToggle: onToggleHandler,
    className: getClassNameList('uitk-expando', `uitk-expando-${type === 'list' ? 'list-item' : type}`, className, {
      'is-disabled': isDisabled
    }),
    ...attrs,
    children: [jsx("summary", {
      className: getClassNameList('uitk-expando-trigger-content'),
      tabIndex: isDisabled ? -1 : undefined,
      children: type === 'link' ? jsx(EGDSLink, {
        size: size,
        className: getClassNameList('uitk-expando-link'),
        align: alignment === 'trailing' ? 'right' : undefined,
        children: jsx("span", {
          children: title
        })
      }) : title
    }), jsx("div", {
      className: getClassNameList('uitk-expando-body', {
        [`is-indented-${triggerPosition}`]: isIndented
      }),
      "data-testid": getTestId(`uitk-expando-${type}-body`, isVisible),
      children: jsx("div", {
        className: getClassNameList('uitk-expando-description'),
        children: expandoDescription ?? children
      })
    })]
  });
};
EGDSExpando.displayName = 'EGDSExpando';
export { EGDSExpando };